// Create a new file called MotionGlossary.tsx

import React, { useState, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { 
  Card, 
  CardContent
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";
import { Search, Filter, X, Info } from "lucide-react";
import { fetchWithAuth } from '../utils/api';
import './MotionGlossary.css';

// Define motion type
interface Theme {
  id: number;
  name: string;
  description?: string;
}

interface Motion {
  id: number;
  theme: Theme;
  text: string;
  created_at: string;
  competition_type: string;
  info_slide?: string;
  has_info_slide: boolean;
  difficulty?: string;
  difficulty_display?: string;
}

const MotionGlossary: React.FC = () => {
  const [motions, setMotions] = useState<Motion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [themeFilter, setThemeFilter] = useState<string>("");
  const [competitionFilter, setCompetitionFilter] = useState<string>("");
  const [difficultyFilter, setDifficultyFilter] = useState<string>("");
  const [availableThemes, setAvailableThemes] = useState<Theme[]>([]);
  const [availableCompetitions, setAvailableCompetitions] = useState<string[]>([]);
  const [selectedMotion, setSelectedMotion] = useState<Motion | null>(null);
  
  // Fetch motions on component mount
  useEffect(() => {
    fetchMotions();
  }, []);

  const fetchMotions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      console.log("Using token:", token);
      
      const data = await fetchWithAuth('/api/motions/glossary/');
      console.log("Motion glossary response:", data);
      setMotions(data.motions || []);
      setAvailableThemes(data.themes || []);
      setAvailableCompetitions(data.competition_types || []);
      setError("");
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching motions");
      console.error("Error fetching motions:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter motions based on search term and filters
  const filteredMotions = motions.filter(motion => {
    const matchesSearch = 
      motion.text.toLowerCase().includes(searchTerm.toLowerCase()) ||
      motion.theme.name.toLowerCase().includes(searchTerm.toLowerCase());
      
    const matchesTheme = themeFilter ? motion.theme.id === parseInt(themeFilter) : true;
    const matchesCompetition = competitionFilter ? motion.competition_type === competitionFilter : true;
    
    // Filter by difficulty if a difficulty filter is selected
    const matchesDifficulty = difficultyFilter 
      ? motion.difficulty_display?.toLowerCase() === difficultyFilter.toLowerCase() || motion.difficulty?.toLowerCase() === difficultyFilter.toLowerCase()
      : true;
    
    return matchesSearch && matchesTheme && matchesCompetition && matchesDifficulty;
  });
  
  // Function to clear all filters
  const clearFilters = () => {
    setThemeFilter("");
    setCompetitionFilter("");
    setDifficultyFilter("");
    setSearchTerm("");
  };

  // Function to open info slide modal
  const openInfoSlide = (motion: Motion) => {
    setSelectedMotion(motion);
  };

  // Function to close info slide modal
  const closeInfoSlide = () => {
    setSelectedMotion(null);
  };

  // Determine difficulty level from the motion data
  const getDifficultyLevel = (motion: Motion) => {
    // Use actual difficulty from backend if available
    if (motion.difficulty_display) {
      return motion.difficulty_display;
    }
    
    if (motion.difficulty) {
      // Convert first letter to uppercase
      return motion.difficulty.charAt(0).toUpperCase() + motion.difficulty.slice(1);
    }
    
    // Fallback for older data without difficulty field
    const motionId = motion.id;
    if (motionId % 3 === 0) return "Advanced";
    if (motionId % 3 === 1) return "Intermediate";
    return "Beginner";
  };

  // Determine difficulty class for styling
  const getDifficultyClass = (difficulty: string) => {
    const lowerDifficulty = difficulty.toLowerCase();
    switch (lowerDifficulty) {
      case "beginner":
        return "difficulty-beginner";
      case "intermediate":
        return "difficulty-intermediate";
      case "advanced":
        return "difficulty-advanced";
      case "expert":
        return "difficulty-expert";
      default:
        return "";
    }
  };

  return (
    <div className="motion-glossary">
      <div className="motion-glossary-header">
        <div className="search-container">
          <div className="search-input-wrapper">
            <Search className="search-icon" size={16} />
            <Input 
              placeholder="Search motions..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  // If needed, add any additional search logic here
                  console.log("Search submitted:", searchTerm);
                }
              }}
            />
          </div>
          <button className="search-button" onClick={() => {
            // If needed, add any additional search logic here
            console.log("Search button clicked:", searchTerm);
          }}>
            Search
          </button>
        </div>
        <div className="filters">
          <div className="filter-group">
            <Label>Theme:</Label>
            <select
              value={themeFilter}
              onChange={(e) => setThemeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All</option>
              {availableThemes.map((theme) => (
                <option key={theme.id} value={theme.id.toString()}>{theme.name}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <Label>Competition:</Label>
            <select
              value={competitionFilter}
              onChange={(e) => setCompetitionFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All</option>
              {availableCompetitions.map((comp) => (
                <option key={comp} value={comp}>{comp}</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <Label>Difficulty:</Label>
            <select
              value={difficultyFilter}
              onChange={(e) => setDifficultyFilter(e.target.value)}
              className="filter-select"
            >
              <option value="">All</option>
              <option value="Beginner">Beginner</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading motions...</p>
        </div>
      ) : error ? (
        <div className="error-message">
          {error}
        </div>
      ) : (
        <>
          <div className="motions-found">
            {filteredMotions.length} motions found
          </div>
          
          <div className="motions-grid">
            {filteredMotions.map((motion) => {
              const difficulty = getDifficultyLevel(motion);
              return (
                <Card key={motion.id} className="motion-card" onClick={() => openInfoSlide(motion)}>
                  <CardContent className="motion-card-content">
                    <div className="motion-text-container">
                      {motion.has_info_slide && (
                        <div 
                          className="info-icon-container"
                          title="Info Slide available"
                        >
                          <Info size={16} className="info-icon" />
                        </div>
                      )}
                      <div className="motion-text">
                        {motion.text}
                      </div>
                    </div>
                    <div className="motion-meta">
                      <div className="theme-label">
                        {motion.theme.name}
                      </div>
                      <div className={`difficulty-label ${getDifficultyClass(difficulty)}`}>
                        {difficulty}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Info Slide Modal */}
      {selectedMotion && (
        <div className="info-slide-modal-overlay" onClick={closeInfoSlide}>
          <div className="info-slide-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal-btn" onClick={closeInfoSlide}>
              <X size={24} />
            </button>
            <h2 className="info-slide-title">{selectedMotion.text}</h2>
            
            <div className="info-slide-content">
              {selectedMotion.has_info_slide && (
                <>
                  <h3>Info Slide</h3>
                  <p>{selectedMotion.info_slide}</p>
                </>
              )}
              
              <div className="info-slide-metadata">
                <div className="metadata-item">
                  <span className="metadata-label">Theme:</span>
                  <span className="metadata-value">{selectedMotion.theme.name}</span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Difficulty:</span>
                  <span className="metadata-value">{getDifficultyLevel(selectedMotion)}</span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Competition:</span>
                  <span className="metadata-value">{selectedMotion.competition_type || "General"}</span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Added:</span>
                  <span className="metadata-value">
                    {new Date(selectedMotion.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="info-slide-actions">
              <Button onClick={() => alert("Practice feature coming soon!")}>
                Practice with this Motion
              </Button>
              <Button variant="outline" onClick={() => alert("Save feature coming soon!")}>
                Save to Collection
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotionGlossary;